/**
 * Logger Utility
 * Guards console statements for production - only logs in development
 *
 * Usage:
 * import { logger } from './lib/logger';
 * logger.log('Debug info');
 * logger.error('Error occurred:', error);
 * logger.warn('Warning message');
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Safe console wrapper that only logs in development
 */
export const logger = {
  /**
   * Log general information (only in development)
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log('[JDG]', ...args);
    }
  },

  /**
   * Log errors - these are logged in both dev and production
   * but with less detail in production
   */
  error: (message, ...args) => {
    if (isDevelopment) {
      console.error('[JDG Error]', message, ...args);
    } else {
      // In production, log a sanitized version without potentially sensitive data
      console.error('[JDG Error]', typeof message === 'string' ? message : 'An error occurred');
    }
  },

  /**
   * Log warnings (only in development)
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[JDG Warning]', ...args);
    }
  },

  /**
   * Log debug information (only in development)
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[JDG Debug]', ...args);
    }
  },

  /**
   * Log info messages (only in development)
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info('[JDG Info]', ...args);
    }
  },

  /**
   * Group related logs together (only in development)
   */
  group: (label) => {
    if (isDevelopment) {
      console.group(`[JDG] ${label}`);
    }
  },

  /**
   * End a console group
   */
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Log with table formatting (only in development)
   */
  table: (data) => {
    if (isDevelopment) {
      console.table(data);
    }
  },

  /**
   * Time a operation (only in development)
   */
  time: (label) => {
    if (isDevelopment) {
      console.time(`[JDG] ${label}`);
    }
  },

  /**
   * End timing (only in development)
   */
  timeEnd: (label) => {
    if (isDevelopment) {
      console.timeEnd(`[JDG] ${label}`);
    }
  }
};

/**
 * Create a scoped logger for a specific module
 * Usage:
 * const log = createScopedLogger('AuthContext');
 * log.info('User logged in');  // Output: [JDG:AuthContext] User logged in
 */
export const createScopedLogger = (scope) => ({
  log: (...args) => logger.log(`[${scope}]`, ...args),
  error: (message, ...args) => logger.error(`[${scope}] ${message}`, ...args),
  warn: (...args) => logger.warn(`[${scope}]`, ...args),
  debug: (...args) => logger.debug(`[${scope}]`, ...args),
  info: (...args) => logger.info(`[${scope}]`, ...args),
});

export default logger;
